import { randomUuid } from '@Core/lib/tasks/utils/text';
import { apiErrorMessage, toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  SdkApiError,
  SdkWorkflowResponse,
  UseWorkflowActionResult
} from '@Pages/pageRuntime/sdk/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { inputFingerprint } from './idempotencyKeys';
import { useWorkflowRequest } from './useWorkflowRequest';

type WorkflowActionState = {
  isPending: boolean;
  errorMessage: string | null;
  failure: SdkApiError | null;
  result: SdkWorkflowResponse | null;
};

const initialState: WorkflowActionState = {
  isPending: false,
  errorMessage: null,
  failure: null,
  result: null
};

/** Runtime implementation of `Sdk.hooks.useWorkflowAction`. */
export function useWorkflowAction({
  workflowId,
  params,
  idempotent = false
}: {
  workflowId: string;
  params?: Record<string, unknown>;
  idempotent?: boolean;
}): UseWorkflowActionResult {
  const queryClient = useQueryClient();
  const { submit, workflowUrl } = useWorkflowRequest(workflowId, params);
  const managedRequestFingerprint = idempotent
    ? inputFingerprint({ route: workflowUrl, params: params ?? {} })
    : '';
  const [state, setState] = useState(initialState);
  const latestRequestId = useRef(0);
  const isMounted = useRef(true);
  const managedIdempotency = useRef<{
    workflowId: string;
    fingerprint: string;
    key: string;
  } | null>(null);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
      latestRequestId.current += 1;
    };
  }, []);

  // Generated pages commonly use `run` in effect dependency arrays, so internal state updates must
  // not change its identity when the resolved request and action options are unchanged.
  // react-doctor-disable-next-line react-compiler-no-manual-memoization
  const run = useCallback(
    (inputs: Record<string, unknown>) => {
      if (!isMounted.current) {
        return Promise.resolve(null);
      }

      let requestInputs = inputs;

      if (idempotent) {
        const managedInputs = { ...inputs };
        delete managedInputs.idempotency_key;
        const fingerprint = inputFingerprint({
          inputs: managedInputs,
          request: managedRequestFingerprint
        });
        let managed = managedIdempotency.current;

        if (
          managed === null ||
          managed.workflowId !== workflowId ||
          managed.fingerprint !== fingerprint
        ) {
          managed = { workflowId, fingerprint, key: randomUuid() };
          managedIdempotency.current = managed;
        }

        requestInputs = {
          ...managedInputs,
          idempotency_key: managed.key
        };
      }

      const requestId = latestRequestId.current + 1;
      latestRequestId.current = requestId;
      setState({ ...initialState, isPending: true });

      return submit(requestInputs).then(
        (response) => {
          void queryClient.invalidateQueries();

          if (isMounted.current && latestRequestId.current === requestId) {
            setState({ ...initialState, result: response });
          }

          return response;
        },
        (error: unknown) => {
          const failure = toApiError(error);

          if (isMounted.current && latestRequestId.current === requestId) {
            setState({
              isPending: false,
              errorMessage: apiErrorMessage(failure, failure.message),
              failure,
              result: null
            });
          }

          return null;
        }
      );
    },
    [idempotent, managedRequestFingerprint, queryClient, submit, workflowId]
  );

  const reset = useCallback(() => {
    latestRequestId.current += 1;
    managedIdempotency.current = null;

    if (isMounted.current) {
      setState(initialState);
    }
  }, []);

  return { run, ...state, reset };
}
